<template>
  <div>
    <PageAllow controller="ReturnOrders" action="add" />
    <Breadcrumbs :items="items" />

    <v-sheet class="transparent-background-color">
      <v-row dense class="mx-5">
        <v-col cols="12" sm="12" md="4">
          <v-row dense align="center" class="pa-1 pe-2">
            <SearcherDatetimeRangeWithRadio
              v-model="options"
              type="datetime"
              show-radios
              :radios="datetimeRadios"
              removeIcon
            />
          </v-row>
        </v-col>
        <v-col cols="12" sm="12" md="8" align-self="center">
          <v-row dense align="center" class="search-controls">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="px-0 py-0 caption font-weight-bold grey--text"
                  color="primary"
                  @click.stop="onSearch()"
                >
                  <v-icon small class="pe-1">fa-search</v-icon>
                  {{ $t("Search.search") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.searchTip") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="caption font-weight-bold grey--text"
                  @click.stop="resetSearch()"
                >
                  <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                  {{ $t("Search.reset") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.resetTip") }}</span>
            </v-tooltip>
            <v-divider vertical />
            <v-btn
              class="caption font-weight-bold grey--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
              small
              text
              @click.stop="$store.commit('toggleSearchPanel')"
            >
              {{ $t("Search.pro") }}
              <v-icon small class="ps-1">{{
                $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
              }}</v-icon>
            </v-btn>
            <v-spacer />
          </v-row>
        </v-col>
      </v-row>
      <v-expansion-panels
        v-model="$store.getters.panels"
        multiple
        flat
        accordion
      >
        <v-expansion-panel class="transparent-background-color">
          <v-expansion-panel-content>
            <v-row dense align="center">
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="order_code"
                  v-model="options.order_code"
                  @text="orderCodeKeyword = $event"
                  :label="$t('ReturnOrderApply.Fields.order_code')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="shop_keyword"
                  v-model="options.shop_keyword"
                  @text="shopKeyword = $event"
                  :label="$t('ReturnOrderApply.Fields.shop_keyword', [$consts.shopName()])"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="material_keyword"
                  v-model="options.material_keyword"
                  @text="materialKeyword = $event"
                  :label="$t('ReturnOrderApply.Fields.material_keyword')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherStatus
                  v-model="options.status"
                  :label="$t('Order.status.title')"
                  :items="$consts.order.status.filter(
                  (e) => e.value != 1 && e.value != 2 && e.value != 3
                  )"
                />
              </v-col>

            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <div class="mx-4 mb-4">
        <v-data-table
          :headers="headers"
          :items="orderDetails"
          :options.sync="options"
          :page.sync="paging.page"
          :server-items-length="paging.count"
          :loading="loading"
          mobile-breakpoint="800"
          multi-sort
          class="elevation-2 px-4 pt-1 sticky-table"
          id="sticky-table"
          :class="stickTableCols ? 'sticky-first-col sticky-last-col' : ''"
          fixed-header
          :footer-props="$t('$config.footer-props')"
        >
          <template v-slot:header.actions="{ header }">
            {{ header.text }}
            <v-icon x-small>fa-sliders-h</v-icon>
          </template>
          <template v-slot:item.order_code="{ item, index }">
            <span
              class="text-no-wrap caption font-weight-bold"
              :v-resize="!index ? $stickTableResizer() : null"
            >
              {{ item.order_code }}
            </span>
            <br />
            <span
              class="text-no-wrap caption secondary--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
              v-if="options.datetimeRadioSelected == 'created'"
            >
              {{ item.created | formatTime }}
            </span>
            <span
              class="text-no-wrap caption secondary--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
              v-if="options.datetimeRadioSelected == 'actual'"
            >
              {{ item.actual_date | formatTime }}
            </span>
          </template>
          <template v-slot:item.order_price="{ item }">
            {{ $t("Global.money") }}{{ item.order_price | formatMoney }}
          </template>
          <template v-slot:item.shop_name="{ item }">
            <span class="text-no-wrap">
              {{ item.shop_name }}
            </span>
            <br />
            <span class="text-no-wrap caption secondary--text">
              {{ item.shop_code }}
            </span>
          </template>
          <template v-slot:item.material_name="{ item }">
            <span class="text-no-wrap">
              {{ item.material_name }}
            </span>
            <br />
            <span class="text-no-wrap caption secondary--text">
              {{ item.material_code }}
            </span>
          </template>
          <template v-slot:item.order_status="{ item }">
            <v-chip
              small
              label
              :color="item.order_status | orderStatusColor"
              :text-color="item.order_status | orderStatusTextColor"
            >{{ item.order_status | orderStatusText }}
            </v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn
              small
              color="info"
              class="px-2 me-2"
              :href="'/#/return-order-new?order_id=' + item.order_id + '&order_code=' + item.order_code + '&shop_id=' + item.shop_id + '&material_id=' + item.material_id"
            >
              <v-icon x-small class="pe-1">fa-truck-loading</v-icon>
              {{ $t("ReturnOrderApply.Fields.apply") }}
            </v-btn>
          </template>
        </v-data-table>
      </div>
    </v-sheet>

  </div>
</template>

<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import SearcherDatetimeRangeWithRadio from "@/components/plugins/SearcherDatetimeRangeWithRadio";
import SearcherKeyword from "@/components/plugins/SearcherKeyword";
import ReturnOrders from "@/api/return_orders";
import SearcherStatus from "@/components/plugins/SearcherStatus.vue";

const defaultOptions = {
  datetimeRadioSelected: "created",
  created_time_from:
    new Date(new Date().getTime() - 7 * 24 * 3600 * 1000)
      .toISOString()
      .substr(0, 10) + " 00:00:00",
  created_time_to: new Date().toISOString().substr(0, 10) + " 23:59:59",
  actual_time_from: "",
  actual_time_to: "",
  order_code: "",
  shop_keyword: "",
  material_keyword: "",
  disable_return_flgs: [0],
  status: [4, 5, 6],
  cancel_flgs: [0],
};


export default {
  components: {
    SearcherStatus,
    PageAllow,
    Breadcrumbs,
    SearcherDatetimeRangeWithRadio,
    SearcherKeyword,
  },

  computed: {
    items: () => [
      {
        text: i18n.t("Nav.home"),
        disabled: false,
        href: "/home",
      },
      {
        text: i18n.t("Nav.returnOrderManagement"),
        disabled: false,
        replace: true,
        href: "/return-orders",
      },
      {
        text: i18n.t("Nav.returnOrderApply"),
        disabled: true,
        href: "/return-orders-apply",
      }
    ],
    datetimeRadios: () => [
      {
        key: "created",
        text: i18n.t("Order.Fields.created_time"),
      },
      {
        key: "actual",
        text: i18n.t("Order.Fields.actual_time"),
      }
    ],
    headers() {
      return [
        {
          text:
            i18n.t("ReturnOrderApply.Fields.order_code") +
            " / " +
              this.datetimeRadios.find(
                  (s) => s.key == this.options.datetimeRadioSelected
              ).text,
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "order_code",
        },
        {
          text: i18n.t("ReturnOrderApply.Fields.shop", [this.$consts.shopName()]),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "shop_name",
        },
        {
          text: i18n.t("ReturnOrderApply.Fields.order_price"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "order_price",
        },
        {
          text: i18n.t("ReturnOrderApply.Fields.material"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "material_name",
        },
        {
          text: i18n.t("ReturnOrderApply.Fields.order_status"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "order_status"
        },
        {
          text: i18n.t("TableHeaders.actions"),
          align: "center",
          sortable: false,
          value: "actions",
          width: "100px",
        },
      ];
    },
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading", "settings", "stickTableCols"]),
  },

  mounted() {

  },

  data: () => ({
    orderCodeKeyword: "",
    shopKeyword: "",
    materialKeyword: "",
    options: JSON.parse(
      sessionStorage.getItem("return-orders-apply-options")
        ? sessionStorage.getItem("return-orders-apply-options")
        : JSON.stringify(defaultOptions)
    ),
    paging: {},
    orderDetails: [],
  }),

  watch: {
    options: {
      // immediate: true, //为避免覆盖session里保存的options，不能启用immediate
      deep: true,
      handler() {
        this.getOrdersFromAPI();
        sessionStorage.setItem(
          "return-orders-apply-options",
          JSON.stringify(this.options)
        );
      },
    },
  },

  methods: {
    resetSearch() {
      this.orderCodeKeyword = "";
      this.shopKeyword = "";
      this.materialKeyword = "";
      this.options = JSON.parse(JSON.stringify(defaultOptions));
      [
        this.$refs.order_code,
        this.$refs.shop_keyword,
        this.$refs.material_keyword,
      ].forEach((k) => {
        if (k) k.clear();
      });
    },
    onSearch() {
      this.options = Object.assign({}, this.options, {
        order_code: this.orderCodeKeyword,
        shop_keyword: this.shopKeyword,
        material_keyword: this.materialKeyword,
      });
    },
    getOrdersFromAPI() {
      ReturnOrders.getReturnableList(this.options).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.orderDetails = response.data.order_details;
            this.paging = response.data.paging;
          } else if (response.data.error.code.isNoResultSet()) {
            this.options.page = 1;
          }
        }.bind(this)
      );
    },
  },
};
</script>
